import { useCallback, useState } from 'react';
import PlacementCelebrationToast, {
  PlacementCelebrationPayload,
} from './PlacementCelebrationToast';
import styles from './PlacementCelebration.module.scss';

interface PlacementCelebrationContainerProps {
  toasts: PlacementCelebrationPayload[];
  onDismiss: (id: string) => void;
}

const PlacementCelebrationContainer = ({
  toasts,
  onDismiss,
}: PlacementCelebrationContainerProps): JSX.Element | null => {
  if (toasts.length === 0) return null;

  return (
    <div className={styles.container} aria-live="polite">
      {toasts.map((payload) => (
        <PlacementCelebrationToast
          key={payload.id}
          payload={payload}
          onDismiss={onDismiss}
        />
      ))}
    </div>
  );
};

export default PlacementCelebrationContainer;

// Hook that owns the toast queue state for use by the consumer (MainLayout)
export function usePlacementCelebrations(): {
  toasts: PlacementCelebrationPayload[];
  enqueueCelebration: (payload: PlacementCelebrationPayload) => void;
  dismissCelebration: (id: string) => void;
} {
  const [toasts, setToasts] = useState<PlacementCelebrationPayload[]>([]);

  const enqueueCelebration = useCallback(
    (payload: PlacementCelebrationPayload) => {
      setToasts((prev) => {
        // Dedupe by id
        if (prev.some((t) => t.id === payload.id)) return prev;
        return [...prev, payload];
      });
    },
    [],
  );

  const dismissCelebration = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return { toasts, enqueueCelebration, dismissCelebration };
}
